import React, { useState, useEffect } from 'react';
import { Input, Button, Tag, Space, Typography, message, Modal } from 'antd';
import { EditOutlined, CloseOutlined } from '@ant-design/icons';

const { Text } = Typography;

const EnumValuesEditor = ({
                              fieldType,
                              value = [],
                              onChange,
                          }) => {
    const [newValue, setNewValue] = useState('');
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [bulkEditValues, setBulkEditValues] = useState('');
    const [localValues, setLocalValues] = useState(value || []);

    useEffect(() => {
        setLocalValues(value || []);
    }, [value]);

    const validateValue = (val) => {
        if (!val.trim()) return { valid: false, message: 'Value cannot be empty' };

        switch (fieldType) {
            case 'number':
            case 'decimal':
                if (isNaN(Number(val))) return { valid: false, message: 'Must be a number' };
                break;
            case 'boolean':
                const lower = val.toLowerCase().trim();
                if (!['true', 'false', 'yes', 'no', '1', '0'].includes(lower)) {
                    return { valid: false, message: 'Must be a boolean value' };
                }
                break;
        }

        return { valid: true };
    };

    const triggerChange = (newValues) => {
        setLocalValues(newValues);
        onChange?.(newValues);
    };

    const handleAddValue = () => {
        if (!newValue.trim()) return;

        const valuesToAdd = newValue
            .split(',')
            .map(v => v.trim())
            .filter(v => !!v);

        let added = 0;
        let duplicate = 0;
        let invalid = 0;
        const updated = [...localValues];

        for (const val of valuesToAdd) {
            const { valid, message: msg } = validateValue(val);
            if (!valid) {
                invalid++;
                continue;
            }

            if (updated.includes(val)) {
                duplicate++;
                continue;
            }

            updated.push(val);
            added++;
        }

        if (added > 0) {
            triggerChange(updated);
            message.success(`Added ${added} value(s)`);
        }

        if (duplicate > 0) message.info(`${duplicate} duplicate(s) skipped`);
        if (invalid > 0) message.error(`${invalid} invalid value(s)`);

        setNewValue('');
    };

    const handleRemoveValue = (val) => {
        const updated = localValues.filter(v => v !== val);
        triggerChange(updated);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddValue();
        }
    };

    const openBulkEditModal = () => {
        setBulkEditValues(localValues.join('\n'));
        setEditModalVisible(true);
    };

    const saveBulkEdit = () => {
        const values = bulkEditValues
            .split('\n')
            .map(v => v.trim())
            .filter(v => !!v);

        const validValues = [];
        const invalidValues = [];

        for (const val of values) {
            const { valid } = validateValue(val);
            if (valid) validValues.push(val);
            else invalidValues.push(val);
        }

        if (invalidValues.length > 0) {
            message.warning(`${invalidValues.length} invalid value(s) skipped`);
        }

        triggerChange(validValues);
        setEditModalVisible(false);
    };

    return (
        <>
            <div style={{ marginBottom: 12 }}>
                <Space.Compact style={{ width: '100%' }}>
                    <Input
                        placeholder={`Enter values (comma-separated)`}
                        value={newValue}
                        onChange={(e) => setNewValue(e.target.value)}
                        onKeyPress={handleKeyPress}
                    />
                    <Button type="primary" onClick={handleAddValue}>Add</Button>
                </Space.Compact>
            </div>

            <div style={{ maxHeight: '120px', overflowY: 'auto', marginTop: 8 }}>
                {localValues.length > 0 ? (
                    localValues.map((val, idx) => (
                        <Tag
                            key={idx}
                            closable
                            onClose={() => handleRemoveValue(val)}
                            style={{ margin: '0 8px 8px 0' }}
                        >
                            {val}
                        </Tag>
                    ))
                ) : (
                    <Text type="secondary">No values defined</Text>
                )}
            </div>

            {localValues.length > 0 && (
                <Button
                    type="link"
                    onClick={openBulkEditModal}
                    icon={<EditOutlined />}
                    style={{ padding: '8px 0' }}
                >
                    Bulk Edit
                </Button>
            )}

            <Modal
                title="Edit Allowed Values"
                open={editModalVisible}
                onOk={saveBulkEdit}
                onCancel={() => setEditModalVisible(false)}
            >
                <p>Enter one value per line:</p>
                <Input.TextArea
                    rows={10}
                    value={bulkEditValues}
                    onChange={(e) => setBulkEditValues(e.target.value)}
                />
            </Modal>
        </>
    );
};

export default EnumValuesEditor;
